import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Mic,
  Square,
  Loader2,
  Sparkles,
  Check,
  ArrowLeft,
  Calendar as CalendarIcon,
  Clock,
  Tag as TagIcon,
  Repeat,
  AlertCircle,
  X,
  Type,
  Sliders,
  Send,
  Undo2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiError } from "../lib/api";
import { useTasks } from "../contexts/TaskContext";
import { useAuth } from "../contexts/AuthContext";
import { getWorkspace, WORKSPACES } from "../lib/workspaces";
import { useWorkLabel } from "../lib/workLabel";
import { preWarmMic, isPreWarmed } from "../lib/micPrewarm";
import { toast } from "sonner";
import { emitUpgradeRequired } from "../components/UpgradeRequiredDialog";

const AUTO_SAVE_CONFIDENCE = 0.78; // ≥ this and we auto-save; else preview

const PRIORITY_COLORS = {
  urgent: "#EF4444",
  important: "#F59E0B",
  normal: "#0281FF",
  low: "#64748B",
};

const padded = (n) => String(n).padStart(2, "0");
const formatElapsed = (s) => `${padded(Math.floor(s / 60))}:${padded(s % 60)}`;

export default function RapidCapturePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { createTask } = useTasks();
  const { user } = useAuth();
  const workLabel = useWorkLabel({ isAuthed: !!user });

  const modeParam = params.get("mode") || "voice"; // voice | text
  const prefillParam = params.get("prefill") || params.get("title") || "";
  const shareUrlParam = params.get("share_url") || "";

  // ---- State machine: idle → recording → processing → preview → saved ----
  const [stage, setStage] = useState(prefillParam ? "text" : modeParam); // initial mode
  const [elapsed, setElapsed] = useState(0);
  const [textInput, setTextInput] = useState(prefillParam);
  const [transcript, setTranscript] = useState("");
  const [parsed, setParsed] = useState(null);
  const [savingTask, setSavingTask] = useState(false);
  const [savedTask, setSavedTask] = useState(null); // for the undo toast
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [showEditor, setShowEditor] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const stopTimerRef = useRef(null);
  const autoStartedRef = useRef(false);
  const redirectTimerRef = useRef(null);

  const MAX_RECORDING_MS = 90_000;

  // ---- Mic pre-warm + auto-start when /capture?mode=voice ----
  useEffect(() => {
    if (modeParam !== "voice") return;
    if (autoStartedRef.current) return;
    autoStartedRef.current = true;
    // If pre-warmed, start immediately; otherwise warm then start.
    (async () => {
      if (!isPreWarmed()) {
        const ok = await preWarmMic();
        if (!ok) {
          setPermissionDenied(true);
          return;
        }
      }
      // Tiny delay so the user can see the "Listening…" state animate in
      setTimeout(() => {
        startRecording();
      }, 220);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeParam]);

  // Cleanup
  useEffect(() => {
    return () => {
      try { mediaRecorderRef.current?.stop(); } catch {}
      if (tickRef.current) clearInterval(tickRef.current);
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, []);

  // If user landed with prefill from share-target, auto-parse text on mount
  useEffect(() => {
    if (prefillParam && !parsed) {
      runTextParse(prefillParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------- Audio recording --------------------
  const startRecording = useCallback(async () => {
    if (stage === "recording" || stage === "processing") return;
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
    } catch (e) {
      setPermissionDenied(true);
      return;
    }
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
      "audio/ogg;codecs=opus",
    ];
    const supported = candidates.find((m) => MediaRecorder.isTypeSupported?.(m)) || "";
    const mr = new MediaRecorder(stream, supported ? { mimeType: supported } : undefined);
    chunksRef.current = [];
    mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    mr.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      if (tickRef.current) clearInterval(tickRef.current);
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
      tickRef.current = null;
      stopTimerRef.current = null;

      const cleanMime = (mr.mimeType || "audio/webm").split(";")[0];
      const ext = cleanMime.split("/")[1] || "webm";
      const blob = new Blob(chunksRef.current, { type: cleanMime });
      chunksRef.current = [];
      if (!blob.size) {
        setStage("voice");
        setElapsed(0);
        toast.error("No audio captured — try again");
        return;
      }
      setStage("processing");
      try {
        const fd = new FormData();
        fd.append("audio", blob, `voice.${ext}`);
        const { data } = await api.post("/tasks/voice", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60_000,
        });
        setTranscript(data?.transcript || "");
        const p =
          data?.parsed && Object.keys(data.parsed).length > 0
            ? data.parsed
            : { title: data?.transcript || "" };
        setParsed(p);
        await handleParsed(p);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Voice processing failed");
        setStage("voice");
      } finally {
        setElapsed(0);
      }
    };
    mediaRecorderRef.current = mr;
    mr.start();
    setStage("recording");
    setElapsed(0);
    const startedAt = Date.now();
    tickRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 250);
    stopTimerRef.current = setTimeout(() => {
      try { mr.stop(); } catch {}
      toast.message("Hit the 90s cap — wrapping up");
    }, MAX_RECORDING_MS);
  }, [stage]); // eslint-disable-line react-hooks/exhaustive-deps

  const stopRecording = () => {
    try { mediaRecorderRef.current?.stop(); } catch {}
  };

  const cancelRecording = () => {
    try {
      chunksRef.current = [];
      mediaRecorderRef.current?.stream?.getTracks?.().forEach((t) => t.stop());
      mediaRecorderRef.current?.stop();
    } catch {}
    if (tickRef.current) clearInterval(tickRef.current);
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    setStage("voice");
    setElapsed(0);
  };

  // -------------------- Text smart-input --------------------
  const runTextParse = async (textOverride) => {
    const text = (textOverride ?? textInput).trim();
    if (!text) return;
    setStage("processing");
    setTranscript(text);
    try {
      const { data } = await api.post("/tasks/parse", { text });
      setParsed(data);
      await handleParsed(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't parse — try rephrasing");
      setStage("text");
    }
  };

  // -------------------- Hybrid save logic --------------------
  // Auto-save when confidence ≥ threshold and we have a sane workspace + title
  // Otherwise show preview/editor
  const handleParsed = async (p) => {
    const confidence = Number(p?.confidence ?? 0);
    const missingCritical = !p?.title || !p?.dashboard;
    const lowConfidence = confidence < AUTO_SAVE_CONFIDENCE;
    if (missingCritical || lowConfidence) {
      setStage("preview");
      setShowEditor(true);
      return;
    }
    await saveTask(p);
  };

  const saveTask = async (p) => {
    setSavingTask(true);
    try {
      const baseDescription = [
        transcript && transcript !== p.title ? `Heard: "${transcript}"` : "",
        shareUrlParam ? `Source: ${shareUrlParam}` : "",
      ]
        .filter(Boolean)
        .join("\n");
      const payload = {
        title: p.title,
        description: baseDescription,
        priority: p.priority || "normal",
        dashboard: p.dashboard || "personal",
        status: "todo",
        due_date: p.due_date || null,
        due_time: p.due_time || null,
        due_times: p.due_times || (p.due_time ? [p.due_time] : null),
        start_time: p.start_time || null,
        end_time: p.end_time || null,
        recurrence: p.recurrence || null,
        tags: p.tags || [],
      };
      const created = await createTask(payload);
      setSavedTask(created);
      setStage("saved");
      // Build undo toast with action
      const wsLabel =
        created.dashboard === "work" && workLabel
          ? workLabel
          : getWorkspace(created.dashboard).label;
      toast.success(`Saved to ${wsLabel}`, {
        description: created.title,
        duration: 5000,
        action: {
          label: "Undo",
          onClick: () => undoSavedTask(created),
        },
      });
      // Auto-return to dashboard AFTER the toast Undo window so users can revert.
      redirectTimerRef.current = setTimeout(() => {
        navigate(`/${created.dashboard || "personal"}`);
      }, 5200);
    } catch (e) {
      const status = e?.response?.status;
      const friendly = formatApiError(e?.response?.data?.detail);
      if (status === 402) {
        emitUpgradeRequired({
          title: "Task limit reached",
          message:
            friendly ||
            "Free accounts are capped at 50 tasks. Upgrade to Pro for unlimited tasks, or delete a few existing ones to make room.",
          used: 50,
          limit: 50,
          unit: "tasks",
        });
      } else {
        toast.error(friendly || "Couldn't save task");
      }
      setStage("preview");
      setShowEditor(true);
    } finally {
      setSavingTask(false);
    }
  };

  const undoSavedTask = async (t) => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
    try {
      await api.delete(`/tasks/${t.id}`);
      toast.message("Task removed");
      // Stay on /capture for another quick entry — reset to voice mode
      setSavedTask(null);
      setParsed(null);
      setTranscript("");
      setStage(modeParam);
      setShowEditor(false);
    } catch (e) {
      toast.error("Couldn't undo — task remains");
    }
  };

  // -------------------- Preview field editing --------------------
  const updateField = (key, value) => {
    setParsed((p) => ({ ...(p || {}), [key]: value }));
  };

  // -------------------- Render --------------------
  const ws = getWorkspace(parsed?.dashboard || "personal");
  const wsColor = ws.color;
  const wsDisplayLabel =
    (parsed?.dashboard === "work" && workLabel) ? workLabel : ws.label;

  return (
    <div
      data-testid="capture-page"
      className="min-h-[100dvh] bg-background text-foreground flex flex-col"
      style={{
        paddingTop: "max(1rem, env(safe-area-inset-top))",
        paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))",
      }}
    >
      {/* Top bar */}
      <header className="px-4 sm:px-6 flex items-center justify-between gap-2 mb-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="capture-back-btn"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground">
          Rapid Capture
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => {
              setStage((s) => (s === "text" ? "voice" : "text"));
              setTranscript("");
              setParsed(null);
            }}
            data-testid="capture-mode-toggle"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground px-2 py-1 rounded border border-border"
          >
            {stage === "text" ? <Mic size={12} /> : <Type size={12} />}
            {stage === "text" ? "Voice" : "Text"}
          </button>
        </div>
      </header>

      {/* Main stage */}
      <main className="flex-1 flex flex-col items-stretch justify-center px-5 sm:px-8 max-w-xl mx-auto w-full">
        {/* Permission denied */}
        {permissionDenied && (
          <div
            data-testid="capture-permission-denied"
            className="rounded-lg border border-red-500/40 bg-red-500/5 p-4 mb-5 flex items-start gap-2"
          >
            <AlertCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
            <div className="text-sm">
              <div className="font-medium text-red-500">Microphone blocked</div>
              <p className="text-xs text-muted-foreground mt-1">
                Enable mic access in your browser settings, or switch to text mode below.
              </p>
            </div>
          </div>
        )}

        {/* VOICE mode: huge mic button */}
        {(stage === "voice" || stage === "recording") && !showEditor && (
          <div
            data-testid="capture-voice-stage"
            className="flex flex-col items-center text-center select-none"
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4">
              {stage === "recording" ? "Listening" : "Tap to speak"}
            </p>

            <button
              type="button"
              onClick={
                stage === "recording" ? stopRecording : startRecording
              }
              disabled={permissionDenied}
              data-testid={
                stage === "recording" ? "capture-stop-btn" : "capture-start-btn"
              }
              className={`relative inline-flex items-center justify-center rounded-full transition-all duration-300 active:scale-95 ${
                stage === "recording"
                  ? "h-44 w-44 bg-red-500 text-white shadow-[0_0_0_18px_rgba(239,68,68,0.18)] animate-[capture-pulse_1.6s_ease-in-out_infinite]"
                  : "h-44 w-44 text-white shadow-2xl hover:opacity-95"
              }`}
              style={
                stage === "recording"
                  ? undefined
                  : {
                      background: `radial-gradient(circle at 30% 30%, ${wsColor}, #0a0a0a)`,
                      boxShadow: `0 0 0 16px ${wsColor}1A, 0 12px 60px ${wsColor}33`,
                    }
              }
            >
              {stage === "recording" ? (
                <Square size={48} strokeWidth={2} />
              ) : (
                <Mic size={56} strokeWidth={1.8} />
              )}
              {stage === "recording" && (
                <span className="absolute inset-0 rounded-full border-2 border-white/30 animate-ping" />
              )}
            </button>

            {/* Elapsed timer */}
            <div className="h-6 mt-7 text-xl font-mono tabular-nums">
              {stage === "recording" ? (
                <span className="text-red-500" data-testid="capture-elapsed">
                  {formatElapsed(elapsed)}
                </span>
              ) : (
                <span className="text-muted-foreground/70 text-xs uppercase tracking-widest">
                  Whisper · 90 s max
                </span>
              )}
            </div>

            {stage === "recording" && (
              <button
                type="button"
                onClick={cancelRecording}
                data-testid="capture-cancel-recording"
                className="mt-6 text-xs uppercase tracking-wider text-muted-foreground hover:text-red-500 transition"
              >
                Cancel
              </button>
            )}

            <p className="mt-10 text-xs text-muted-foreground max-w-xs leading-relaxed">
              Try: "Buy heavy cream tomorrow at 9 AM" · "Weekly inventory every
              Monday at 8" · "Pick up kids from soccer at 5pm"
            </p>
          </div>
        )}

        {/* TEXT mode */}
        {stage === "text" && !showEditor && (
          <div data-testid="capture-text-stage" className="space-y-4">
            <h2 className="font-display text-3xl font-black tracking-tight">
              Brain → System.
            </h2>
            <p className="text-sm text-muted-foreground">
              Type what you'd like to capture. AI fills the date, time, recurrence,
              workspace and priority.
            </p>
            <Input
              autoFocus
              data-testid="capture-text-input"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  runTextParse();
                }
              }}
              placeholder='e.g. "Restock walk-in fridge Sunday at 10 AM"'
              className="h-14 text-base"
            />
            <Button
              size="lg"
              onClick={() => runTextParse()}
              disabled={!textInput.trim()}
              data-testid="capture-text-submit"
              className="w-full h-12"
            >
              <Send size={14} className="mr-2" /> Capture
            </Button>
          </div>
        )}

        {/* PROCESSING */}
        {stage === "processing" && (
          <div
            data-testid="capture-processing-stage"
            className="flex flex-col items-center text-center py-12"
          >
            <Loader2
              size={36}
              className="animate-spin"
              style={{ color: wsColor }}
            />
            <p className="mt-5 text-xs uppercase tracking-[0.25em] text-muted-foreground">
              {transcript ? "Parsing with AI" : "Transcribing"}
            </p>
            {transcript && (
              <p className="mt-3 text-base font-medium max-w-md leading-snug">
                "{transcript}"
              </p>
            )}
          </div>
        )}

        {/* PREVIEW / EDITOR (low-confidence or missing fields) */}
        {showEditor && parsed && (
          <PreviewEditor
            parsed={parsed}
            transcript={transcript}
            workLabel={workLabel}
            wsColor={wsColor}
            wsDisplayLabel={wsDisplayLabel}
            onChange={updateField}
            onSave={() => saveTask(parsed)}
            onCancel={() => {
              setShowEditor(false);
              setParsed(null);
              setTranscript("");
              setStage(modeParam);
            }}
            saving={savingTask}
          />
        )}

        {/* SAVED — brief confirmation flash */}
        {stage === "saved" && savedTask && (
          <div
            data-testid="capture-saved-stage"
            className="flex flex-col items-center text-center py-10 anim-fade-up"
          >
            <div
              className="h-20 w-20 rounded-full flex items-center justify-center mb-5"
              style={{
                backgroundColor: `${wsColor}1A`,
                border: `2px solid ${wsColor}66`,
                color: wsColor,
              }}
            >
              <Check size={36} strokeWidth={2.4} />
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              Saved
            </div>
            <h2 className="font-display text-2xl font-black tracking-tight mt-1 mb-3">
              {savedTask.title}
            </h2>
            <div className="text-xs text-muted-foreground mb-7">
              In <span style={{ color: wsColor }}>{wsDisplayLabel}</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full max-w-xs">
              <Button
                variant="ghost"
                onClick={() => undoSavedTask(savedTask)}
                data-testid="capture-saved-undo-btn"
                className="flex-1 h-11 text-muted-foreground"
              >
                <Undo2 size={14} className="mr-1.5" /> Undo
              </Button>
              <Button
                onClick={() => {
                  if (redirectTimerRef.current) {
                    clearTimeout(redirectTimerRef.current);
                    redirectTimerRef.current = null;
                  }
                  setSavedTask(null);
                  setParsed(null);
                  setTranscript("");
                  setStage(modeParam);
                  setShowEditor(false);
                }}
                data-testid="capture-saved-another-btn"
                className="flex-1 h-11"
                style={{ backgroundColor: wsColor, color: "#fff" }}
              >
                <Mic size={14} className="mr-1.5" /> Capture another
              </Button>
            </div>
          </div>
        )}
      </main>

      <style>{`
        @keyframes capture-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
}

// -------------------- Preview / Editor --------------------
function PreviewEditor({
  parsed,
  transcript,
  workLabel,
  wsColor,
  wsDisplayLabel,
  onChange,
  onSave,
  onCancel,
  saving,
}) {
  const priorities = ["low", "normal", "important", "urgent"];
  return (
    <div data-testid="capture-preview-editor" className="space-y-5 anim-fade-up">
      <div className="flex items-start gap-3">
        <Sliders size={16} className="text-muted-foreground mt-1" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground">
            Review before saving
          </div>
          {transcript && (
            <p className="text-xs text-muted-foreground mt-1 italic">
              Heard: "{transcript}"
            </p>
          )}
        </div>
      </div>

      <div>
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Title
        </Label>
        <Input
          autoFocus
          data-testid="capture-preview-title"
          value={parsed.title || ""}
          onChange={(e) => onChange("title", e.target.value)}
          className="mt-1.5 h-11 text-base font-medium"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <CalendarIcon size={10} className="inline mr-1" /> Date
          </Label>
          <Input
            type="date"
            data-testid="capture-preview-date"
            value={parsed.due_date || ""}
            onChange={(e) => onChange("due_date", e.target.value || null)}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <Clock size={10} className="inline mr-1" /> Time
          </Label>
          <Input
            type="time"
            data-testid="capture-preview-time"
            value={parsed.due_time || ""}
            onChange={(e) => onChange("due_time", e.target.value || null)}
            className="mt-1.5"
          />
        </div>
      </div>

      {/* Workspace chips */}
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Workspace
        </Label>
        <div className="mt-1.5 flex gap-2">
          {Object.values(WORKSPACES).map((w) => {
            const isActive = (parsed.dashboard || "personal") === w.key;
            const label = w.key === "work" && workLabel ? workLabel : w.label;
            return (
              <button
                key={w.key}
                type="button"
                onClick={() => onChange("dashboard", w.key)}
                data-testid={`capture-preview-ws-${w.key}`}
                className="flex-1 h-10 rounded-md text-xs font-medium border transition-all"
                style={
                  isActive
                    ? {
                        backgroundColor: `${w.color}1A`,
                        borderColor: `${w.color}66`,
                        color: w.color,
                      }
                    : {
                        borderColor: "hsl(var(--border))",
                        color: "hsl(var(--muted-foreground))",
                      }
                }
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Priority chips */}
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Priority
        </Label>
        <div className="mt-1.5 grid grid-cols-4 gap-2">
          {priorities.map((p) => {
            const isActive = (parsed.priority || "normal") === p;
            return (
              <button
                key={p}
                type="button"
                onClick={() => onChange("priority", p)}
                data-testid={`capture-preview-prio-${p}`}
                className="h-9 rounded-md text-[10px] font-bold uppercase tracking-wider border transition-all"
                style={
                  isActive
                    ? {
                        backgroundColor: `${PRIORITY_COLORS[p]}1A`,
                        borderColor: `${PRIORITY_COLORS[p]}66`,
                        color: PRIORITY_COLORS[p],
                      }
                    : {
                        borderColor: "hsl(var(--border))",
                        color: "hsl(var(--muted-foreground))",
                      }
                }
              >
                {p}
              </button>
            );
          })}
        </div>
      </div>

      {/* Recurrence summary (read-only chip if AI detected one) */}
      {parsed.recurrence?.freq && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Repeat size={12} />
          <span>
            Repeats:{" "}
            <span className="text-foreground font-medium">
              {parsed.recurrence.freq}
              {parsed.recurrence.interval > 1
                ? ` × ${parsed.recurrence.interval}`
                : ""}
            </span>
          </span>
          <button
            type="button"
            onClick={() => onChange("recurrence", null)}
            data-testid="capture-preview-rec-clear"
            className="ml-auto p-1 rounded hover:bg-secondary"
          >
            <X size={11} />
          </button>
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <Button
          variant="ghost"
          onClick={onCancel}
          data-testid="capture-preview-discard-btn"
          className="flex-1 h-12"
        >
          Discard
        </Button>
        <Button
          onClick={onSave}
          disabled={saving || !parsed.title}
          data-testid="capture-preview-save-btn"
          className="flex-1 h-12 font-medium"
          style={{ backgroundColor: wsColor, color: "#fff" }}
        >
          {saving ? (
            <Loader2 size={14} className="animate-spin mr-2" />
          ) : (
            <Check size={14} className="mr-2" />
          )}
          Save to {wsDisplayLabel}
        </Button>
      </div>
    </div>
  );
}
